import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createChart, CrosshairMode, CandlestickSeries, LineSeries, HistogramSeries, AreaSeries } from 'lightweight-charts';
import { Box, Stack, Chip, alpha } from '@mui/material';
import { colors } from '../theme';

const TradingViewChart = ({
  priceHistory,
  ticker,
  height = 500,
  showVolume = true,
  showIndicators = true,
  chartType = 'candlestick'
}) => {
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const candlestickSeriesRef = useRef(null);
  const lineSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const sma20SeriesRef = useRef(null);
  const sma50SeriesRef = useRef(null);
  const resizeObserverRef = useRef(null);

  const [currentChartType, setCurrentChartType] = useState(chartType);

  // Calculate SMA
  const calculateSMA = (data, period) => {
    const result = [];
    for (let i = period - 1; i < data.length; i++) {
      const sum = data.slice(i - period + 1, i + 1).reduce((acc, val) => acc + val.close, 0);
      result.push({
        time: data[i].time,
        value: sum / period
      });
    }
    return result;
  };

  // Process price history
  const processData = useCallback(() => {
    if (!priceHistory || priceHistory.length === 0) return null;

    // Sort by date (oldest first)
    const sorted = [...priceHistory].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Filter out invalid data and remove duplicates
    const uniqueDates = new Set();
    const validData = sorted.filter(item => {
      // Check for valid values
      if (!item.date || !item.open || !item.high || !item.low || !item.close) return false;
      if (isNaN(item.open) || isNaN(item.high) || isNaN(item.low) || isNaN(item.close)) return false;
      
      // Check for duplicates
      if (uniqueDates.has(item.date)) return false;
      uniqueDates.add(item.date);
      
      return true;
    });

    if (validData.length === 0) return null;

    // Data is already trading-days-only from the API.
    // Pass dates directly as YYYY-MM-DD strings — lightweight-charts handles natively.
    const candlestickData = validData.map(item => ({
      time: item.date,
      open: parseFloat(item.open),
      high: parseFloat(item.high),
      low: parseFloat(item.low),
      close: parseFloat(item.close)
    }));

    const lineData = validData.map(item => ({
      time: item.date,
      value: parseFloat(item.close)
    }));

    const volumeData = validData.map(item => ({
      time: item.date,
      value: parseFloat(item.volume || 0),
      color: item.close >= item.open 
        ? alpha(colors.stock.positive, 0.6)
        : alpha(colors.stock.negative, 0.6)
    }));

    // Calculate SMAs
    const sma20Data = calculateSMA(candlestickData, 20);
    const sma50Data = calculateSMA(candlestickData, 50);

    return { candlestickData, lineData, volumeData, sma20Data, sma50Data };
  }, [priceHistory]);

  useEffect(() => {
    if (!chartContainerRef.current) return;

    const data = processData();
    if (!data) return;

    // Ensure container has valid size
    const containerWidth = chartContainerRef.current.clientWidth;
    const containerHeight = height;
    
    if (containerWidth === 0 || containerHeight === 0) {
      console.warn('Chart container has zero size');
      return;
    }

    // Create chart
    const chart = createChart(chartContainerRef.current, {
      width: containerWidth,
      height: containerHeight,
      layout: {
        background: { color: '#ffffff' },
        textColor: colors.text.secondary,
      },
      grid: {
        vertLines: { color: alpha(colors.gray[300], 0.2) },
        horzLines: { color: alpha(colors.gray[300], 0.2) },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: colors.valueConfirmation.main,
          width: 1,
          style: 1,
          labelBackgroundColor: colors.valueConfirmation.main,
        },
        horzLine: {
          color: colors.valueConfirmation.main,
          width: 1,
          style: 1,
          labelBackgroundColor: colors.valueConfirmation.main,
        },
      },
      rightPriceScale: {
        borderColor: colors.gray[300],
      },
      leftPriceScale: {
        visible: showVolume,
        borderColor: colors.gray[300],
      },
      timeScale: {
        borderColor: colors.gray[300],
        timeVisible: false,
        secondsVisible: false,
        rightOffset: 5,
        barSpacing: 8,
        minBarSpacing: 4,
        ignoreWhitespaceIndices: true,
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
    });

    chartRef.current = chart;

    // Add main series based on chart type
    let mainSeries;
    if (currentChartType === 'candlestick') {
      mainSeries = chart.addSeries(CandlestickSeries, {
        upColor: colors.stock.positive,
        downColor: colors.stock.negative,
        borderUpColor: colors.stock.positive,
        borderDownColor: colors.stock.negative,
        wickUpColor: colors.stock.positive,
        wickDownColor: colors.stock.negative,
      });
      mainSeries.setData(data.candlestickData);
      candlestickSeriesRef.current = mainSeries;
    } else {
      mainSeries = chart.addSeries(AreaSeries, {
        topColor: alpha(colors.stock.positive, 0.3),
        bottomColor: alpha(colors.stock.positive, 0.05),
        lineColor: colors.stock.positive,
        lineWidth: 2,
      });
      mainSeries.setData(data.lineData);
      lineSeriesRef.current = mainSeries;
    }

    // Add volume histogram
    if (showVolume) {
      const volumeSeries = chart.addSeries(HistogramSeries, {
        color: colors.stock.positive,
        priceFormat: {
          type: 'volume',
        },
        priceScaleId: 'left',
      });
      volumeSeries.setData(data.volumeData);
      volumeSeriesRef.current = volumeSeries;
    }

    // Add SMA indicators
    if (showIndicators) {
      if (data.sma20Data.length > 0) {
        const sma20Series = chart.addSeries(LineSeries, {
          color: colors.valueConfirmation.main,
          lineWidth: 2,
        });
        sma20Series.setData(data.sma20Data);
        sma20SeriesRef.current = sma20Series;
      }

      if (data.sma50Data.length > 0) {
        const sma50Series = chart.addSeries(LineSeries, {
          color: colors.turnaround.main,
          lineWidth: 2,
        });
        sma50Series.setData(data.sma50Data);
        sma50SeriesRef.current = sma50Series;
      }
    }

    // Fit content
    chart.timeScale().fitContent();

    // Add watermark
    chart.applyOptions({
      watermark: {
        visible: true,
        fontSize: 48,
        horzAlign: 'center',
        vertAlign: 'center',
        color: alpha(colors.gray[400], 0.15),
        text: ticker,
      },
    });

    // Handle resize with debounce to avoid ResizeObserver errors
    const handleResize = () => {
      if (chartContainerRef.current && chartRef.current) {
        // Use requestAnimationFrame to avoid ResizeObserver loop errors
        window.requestAnimationFrame(() => {
          if (chartContainerRef.current && chartRef.current) {
            chartRef.current.applyOptions({
              width: chartContainerRef.current.clientWidth,
            });
          }
        });
      }
    };

    resizeObserverRef.current = new ResizeObserver(handleResize);
    resizeObserverRef.current.observe(chartContainerRef.current);

    // Cleanup
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
      if (chartRef.current) {
        chartRef.current.remove();
      }
    };
  }, [priceHistory, ticker, height, showVolume, showIndicators, currentChartType, processData]);

  // Update chart type
  useEffect(() => {
    setCurrentChartType(chartType);
  }, [chartType]);

  // Calculate latest stats
  const getLatestStats = () => {
    if (!priceHistory || priceHistory.length === 0) return null;
    
    const sorted = [...priceHistory].sort((a, b) => new Date(b.date) - new Date(a.date));
    const latest = sorted[0];
    
    return {
      high: Math.max(...sorted.slice(0, 20).map(d => d.high)),
      low: Math.min(...sorted.slice(0, 20).map(d => d.low)),
      volume: latest.volume,
      date: latest.date
    };
  };

  const stats = getLatestStats();

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: alpha('#fff', 0.0),
      backdropFilter: 'blur(4px)'
    }}>
      {/* Stats and Legend Row - Above Chart */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{ 
          mb: 0.5, 
          px: 2.5,
          bgcolor: alpha('#fff', 0.0),
          backdropFilter: 'blur(4px)',
          py: 0,
          borderRadius: 1
        }}
      >
        {/* Left: H/L/VOL Stats */}
        {stats && (
          <Stack direction="row" spacing={2}>
            <Box sx={{ bgcolor: 'transparent' }}>
              <Box sx={{ fontSize: '0.7rem', color: colors.text.secondary, fontWeight: 600 }}>
                H (20D)
              </Box>
              <Box sx={{ fontSize: '0.9rem', color: colors.stock.positive, fontWeight: 700 }}>
                {stats.high.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Box>
            </Box>
            <Box sx={{ bgcolor: 'transparent' }}>
              <Box sx={{ fontSize: '0.7rem', color: colors.text.secondary, fontWeight: 600 }}>
                L (20D)
              </Box>
              <Box sx={{ fontSize: '0.9rem', color: colors.stock.negative, fontWeight: 700 }}>
                {stats.low.toLocaleString('vi-VN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Box>
            </Box>
            <Box sx={{ bgcolor: 'transparent' }}>
              <Box sx={{ fontSize: '0.7rem', color: colors.text.secondary, fontWeight: 600 }}>
                VOL
              </Box>
              <Box sx={{ fontSize: '0.9rem', color: colors.text.primary, fontWeight: 700 }}>
                {(stats.volume / 1000000).toFixed(1)}M
              </Box>
            </Box>
          </Stack>
        )}

        {/* Right: SMA Legend */}
        {showIndicators && (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label="SMA 20"
              size="small"
              sx={{
                bgcolor: alpha(colors.valueConfirmation.main, 0.1),
                color: colors.valueConfirmation.main,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                '& .MuiChip-label': { px: 1 }
              }}
            />
            <Chip
              label="SMA 50"
              size="small"
              sx={{
                bgcolor: alpha(colors.turnaround.main, 0.1),
                color: colors.turnaround.main,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Stack>
        )}
      </Stack>

      {/* Chart Container */}
      <Box
        ref={chartContainerRef}
        sx={{
          width: '100%',
          flex: 1,
          '& > div': {
            borderRadius: '8px',
          }
        }}
      />
    </Box>
  );
};

export default TradingViewChart;

